import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { extractErrorMessage } from '../api';
import { BetaReaderShell } from '../components/BetaReaderShell';
import { CopyLinkControl } from '../components/CopyLinkControl';
import { DraftPreviewV2Panel } from '../components/DraftPreviewV2Panel';
import { FeedV2Results } from '../components/FeedV2Results';
import { IconLink, IconRss } from '../components/icons';
import { SearchWindowToggle } from '../components/SearchWindowToggle';
import { SortToggle } from '../components/SortToggle';
import { useSortParam } from '../hooks/useSortParam';
import { useReaderRoutes } from '../providers/ReaderRoutesProvider';
import { useAdminMode, useUser } from '../providers/UserProvider';
import { editBetaSearch, getSharedSearch, setSearchIncluded, subscribeSharedSearch, type BetaSearch } from '../repositories/betaSearchRepository';
import { streamFeedV2 } from '../repositories/feedsV2Repository';
import { loadStoredFeedV2 } from '../repositories/signedOutV2Repository';
import { formatAge } from '../utils/formatTime';
import { isPromptExpanded, withPromptExpanded } from '../utils/feedLinks';
import { INITIAL_STATE, type FeedV2State } from '../utils/feedsV2';
import { windowFromParam, WINDOW_OPTIONS, type WindowHours } from '../utils/feedWindow';
import { handOffSearchAttempt, readerEvent, startSearchAttempt } from '../utils/readerTelemetry';
import { FeedPromptBar, SharedFeedAddAction, SharedFeedPromptPanel } from '../components/FeedPromptControls';

/**
 * A saved feed on the feeds v2 engine (`/feeds/<feed id>`, signed in): built for this
 * load from one cosine queue read in waves, judged by the one-pass judge and
 * ordered by rank grade (see `utils/feedsV2`); a public feed with a recent
 * stored build serves that instead, without build details. The feed's controls are the
 * current feed page's: its prompt (edit or search from it), Show in Home, sort,
 * link and exports; for admins, a draft description previewed beside the feed
 * and promoted (`DraftPreviewV2Panel`). A finished load is the feed's stored
 * result, which its RSS and Markdown exports serve. Signed out, it shows the
 * feed's last stored build instead (`utils/signedOutV2`), which the public
 * feed builder refreshes for the public (newsletter) feeds.
 */
export default function FeedV2Page() {
  const { feedId = '' } = useParams();
  return <FeedV2View key={feedId} feedId={feedId} />;
}

function FeedV2View({ feedId }: { feedId: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const routes = useReaderRoutes();
  const [params, setParams] = useSearchParams();
  const hours: WindowHours = windowFromParam(params.get('hours'));
  const { isAdmin, isLoggedIn } = useUser();
  // Signed out: when the stored build shown was made, or that there is none yet.
  const [stored, setStored] = useState<{ builtAt: string | null; notBuilt: boolean } | null>(null);
  const { adminMode } = useAdminMode();
  const [search, setSearch] = useState<BetaSearch | null>(null);
  const [state, setState] = useState<FeedV2State>(INITIAL_STATE);
  const [runKey, setRunKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [promptOpen, setPromptOpen] = useState(() => isPromptExpanded(params));
  const [sort, setSort] = useSortParam();
  // An admin's draft description, built beside the feed until promoted or dismissed.
  const [draft, setDraft] = useState<string | null>(null);
  // Each Preview click builds the draft afresh, the same text included (a retry after a failed preview).
  const [previewRun, setPreviewRun] = useState(0);

  useEffect(() => {
    let disposed = false;
    getSharedSearch(feedId).then(definition => { if (!disposed) setSearch(definition); })
      .catch(err => { if (!disposed) setError(extractErrorMessage(err)); });
    return () => { disposed = true; };
  }, [feedId]);

  useEffect(() => {
    setState(INITIAL_STATE);
    setStored(null);
    const controller = new AbortController();
    const loading = isLoggedIn
      ? streamFeedV2({ feedId, hours, signal: controller.signal, onUpdate: setState })
      : loadStoredFeedV2({ feedId, hours }).then(({ builtAt, notBuilt, ...next }) => {
        if (!controller.signal.aborted) { setState(next); setStored({ builtAt, notBuilt }); }
      });
    loading.catch((err) => {
      if (!controller.signal.aborted) setState((prev) => ({ ...prev, done: true, error: extractErrorMessage(err) }));
    });
    return () => controller.abort();
  }, [feedId, hours, runKey, isLoggedIn]);

  useEffect(() => {
    document.title = search ? `${search.name} · TLDR Reader` : 'TLDR Reader';
  }, [search?.name]);

  const toggleHome = async () => {
    if (busy || !search) return;
    setBusy(true); setError('');
    try {
      if (search.included) {
        await setSearchIncluded(feedId, false); setSearch({ ...search, included: false });
        readerEvent('feed_home_toggle', { feed_id: feedId, on: false, method: 'feeds_switch', feeds_v2: true });
      } else {
        setSearch(await subscribeSharedSearch(feedId));
        readerEvent('feed_add', { search_id: feedId, method: 'feed_page_button', action: 'subscribe', show_in_home: true, feeds_v2: true });
      }
    } catch (err) { setError(extractErrorMessage(err)); }
    finally { setBusy(false); }
  };

  // A saved prompt is a new brief: build the feed again for it.
  const savePrompt = async (text: string) => {
    if (busy || !search || text.trim().length <= 1) return;
    setBusy(true); setError('');
    try {
      setSearch(await editBetaSearch(search.id, { description: text.trim(), revision: search.revision }));
      setRunKey(key => key + 1);
    } catch (err) { setError(`Could not save changes: ${extractErrorMessage(err)}`); }
    finally { setBusy(false); }
  };

  // Promoting makes the draft the feed's description; the feed builds again under it.
  const promoteDraft = async () => {
    if (busy || !search || !draft) return;
    setBusy(true); setError('');
    try {
      setSearch(await editBetaSearch(search.id, { description: draft, revision: search.revision }));
      setDraft(null);
      setRunKey(key => key + 1);
    } catch (err) { setError(`Could not promote the draft: ${extractErrorMessage(err)}`); }
    finally { setBusy(false); }
  };

  const runSearch = (text: string) => {
    const attempt = startSearchAttempt('feed_edit', text, hours);
    navigate(`${routes.newSearch}?${new URLSearchParams({ q: text.trim(), ...(hours === 72 ? { hours: '72' } : {}) })}`,
      { state: handOffSearchAttempt(attempt) });
  };

  const setPromptExpanded = (open: boolean) => {
    setPromptOpen(open);
    setParams(prev => withPromptExpanded(prev, open), { replace: true });
  };

  const selectWindow = (next: number) => setParams(prev => {
    const updated = new URLSearchParams(prev);
    if (next === 72) updated.set('hours', '72'); else updated.delete('hours');
    return updated;
  }, { replace: true });

  const exports = { rss: routes.feedExport(feedId, 'xml', hours), markdown: routes.feedExport(feedId, 'md', hours) };
  return (
    <BetaReaderShell
      exports={exports}
      brandTitle={search?.name}
      feedSwitcher
      storyHotkeys
      search={search ? <FeedPromptBar prompt={search.description} open={promptOpen} onToggle={() => setPromptExpanded(!promptOpen)} /> : undefined}
    >
      {search && <SharedFeedPromptPanel
        search={search}
        open={promptOpen}
        onClose={() => setPromptExpanded(false)}
        onRunSearch={runSearch}
        onSave={(text) => void savePrompt(text)}
        onPreview={(text) => { setDraft(text.trim()); setPreviewRun(run => run + 1); }}
        busy={busy}
        isLoggedIn={isLoggedIn}
        canSave={Boolean(isAdmin || search.is_owner)}
        canPreview={isAdmin}
      />}
      {search && draft && <DraftPreviewV2Panel key={`${draft}:${previewRun}`} draft={draft} hours={hours} live={state}
        busy={busy} onPromote={() => void promoteDraft()} onDismiss={() => setDraft(null)} />}
      <div className="beta-home-controls reader-feed-controls">
        <div className="reader-feed-context">
          {search && <SharedFeedAddAction search={search} isLoggedIn={isLoggedIn} busy={busy} onAdd={() => void toggleHome()} />}
        </div>
        <div className="beta-search-options-bar" role="toolbar" aria-label="Feed options">
          <CopyLinkControl getUrl={() => window.location.href} title="Copy feed link" ariaLabel="Copy feed link"
            copiedMessage="Feed link copied" errorMessage="Could not copy feed link"><IconLink /></CopyLinkControl>
          <SortToggle selected={sort} disabled={state.stories.length === 0} onSelect={setSort} />
          <SearchWindowToggle label="Feed time range" options={WINDOW_OPTIONS} selected={hours} onSelect={selectWindow} />
          <a className="beta-export" href={exports.rss}><IconRss />RSS</a><a className="beta-export reader-mono" href={exports.markdown}>.md</a>
        </div>
      </div>
      {error && <p className="beta-error" role="alert">{error}</p>}
      {stored?.builtAt && <p className="beta-message" role="status">Updated {formatAge(stored.builtAt)}.</p>}
      {stored?.notBuilt && search && (
        <div className="beta-empty">
          <p>This feed hasn’t been built yet.</p>
          <button className="beta-pill selected" type="button"
            onClick={() => navigate('/signup', { state: { backgroundLocation: location, from: location } })}>Sign in to build it</button>
        </div>
      )}
      {!stored?.notBuilt && <FeedV2Results
        state={state}
        sort={sort}
        resetKey={`feeds-v2:${feedId}:${hours}:${runKey}:${sort}`}
        eventContext={{ surface: 'feed', surfaceId: feedId, properties: { feeds_v2: true } }}
        isAdmin={isAdmin && adminMode}
        onRetry={() => setRunKey((key) => key + 1)}
        savedFeed={search ? { id: feedId, name: search.name } : undefined}
      />}
    </BetaReaderShell>
  );
}
